import React, { useEffect, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, XCircle, Star, Clock, ShieldCheck, Download, Smartphone, ChefHat, ArrowRight, X, Check } from 'lucide-react';
const CHECKOUT_URL = 'https://impulsodigital.vip/cart/44769267318870:1';
const HERO_IMAGE = 'https://cdn.magicpatterns.com/uploads/cuuftqUsfWX5tbsVAEKNEc/ChatGPT_Image_21_may_2026,_16_33_19.png';
// --- Components ---
const CTAButton = ({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) => <motion.a href={CHECKOUT_URL} whileHover={{
  scale: 1.02
}} whileTap={{
  scale: 0.98
}} className={`
      block w-full max-w-2xl mx-auto text-center 
      bg-gradient-to-b from-green-500 to-green-600 
      hover:from-green-400 hover:to-green-500
      text-white font-bold text-xl md:text-2xl 
      py-5 px-6 rounded-xl shadow-cta
      border-b-4 border-green-700
      transition-all duration-200
      ${className}
    `} data-id="element-0">
    <span className="flex items-center justify-center gap-2" data-id="element-1">
      {children}
      <ArrowRight className="w-6 h-6 animate-pulse" data-id="element-2" />
    </span>
  </motion.a>;
const Section = ({
  children,
  className = '',
  dark = false
}: {
  children: React.ReactNode;
  className?: string;
  dark?: boolean;
}) => <section className={`py-10 md:py-14 px-4 ${dark ? 'bg-brand-dark text-white' : 'bg-white'} ${className}`} data-id="element-3">
    <div className="max-w-4xl mx-auto" data-id="element-4">{children}</div>
  </section>;
const FadeIn = ({
  children,
  delay = 0
}: {
  children: React.ReactNode;
  delay?: number;
}) => <motion.div initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-100px'
}} transition={{
  duration: 0.6,
  delay
}} data-id="element-5">
    {children}
  </motion.div>;
const LivePopup = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const buyers = [{
    name: 'Lucía M.',
    country: 'Argentina',
    time: '2'
  }, {
    name: 'Sofía R.',
    country: 'México',
    time: '5'
  }, {
    name: 'Camila G.',
    country: 'Colombia',
    time: '12'
  }, {
    name: 'Valentina P.',
    country: 'Chile',
    time: '15'
  }, {
    name: 'Carmen L.',
    country: 'España',
    time: '22'
  }, {
    name: 'Andrea T.',
    country: 'Perú',
    time: '28'
  }, {
    name: 'Florencia D.',
    country: 'Uruguay',
    time: '34'
  }, {
    name: 'Daniela S.',
    country: 'Venezuela',
    time: '41'
  }, {
    name: 'María José V.',
    country: 'Ecuador',
    time: '45'
  }, {
    name: 'Patricia C.',
    country: 'Paraguay',
    time: '52'
  }, {
    name: 'Mónica H.',
    country: 'Costa Rica',
    time: '58'
  }, {
    name: 'Rosa A.',
    country: 'República Dominicana',
    time: '1 hora'
  }];
  useEffect(() => {
    // Initial delay before first popup
    const initialTimer = setTimeout(() => {
      setIsVisible(true);
    }, 4000);
    // Cycle popups
    const cycleTimer = setInterval(() => {
      setIsVisible(false);
      setTimeout(() => {
        setCurrentIndex(prev => (prev + 1) % buyers.length);
        setIsVisible(true);
      }, 1000); // Wait 1s before showing next
    }, 15000); // Show every 15s
    return () => {
      clearTimeout(initialTimer);
      clearInterval(cycleTimer);
    };
  }, []);
  return <AnimatePresence data-id="element-6">
      {isVisible && <motion.div initial={{
      opacity: 0,
      x: -100,
      y: 50
    }} animate={{
      opacity: 1,
      x: 0,
      y: 0
    }} exit={{
      opacity: 0,
      x: -100,
      scale: 0.9
    }} className="fixed bottom-24 md:bottom-6 left-4 md:left-6 z-50 bg-white rounded-lg shadow-2xl border border-gray-100 p-4 max-w-[300px] flex items-start gap-3" data-id="element-7">
          <div className="bg-green-100 p-2 rounded-full shrink-0" data-id="element-8">
            <Check className="w-5 h-5 text-green-600" data-id="element-9" />
          </div>
          <div className="flex-1" data-id="element-10">
            <p className="text-sm font-medium text-gray-800 leading-tight" data-id="element-11">
              <span className="font-bold" data-id="element-12">{buyers[currentIndex].name}</span> de{' '}
              {buyers[currentIndex].country} acaba de comprar el Recetario
              Saludable
            </p>
            <p className="text-xs text-gray-500 mt-1" data-id="element-13">
              hace {buyers[currentIndex].time}{' '}
              {buyers[currentIndex].time === '1 hora' ? '' : 'minutos'}
            </p>
          </div>
          <button onClick={() => setIsVisible(false)} className="text-gray-400 hover:text-gray-600 shrink-0" data-id="element-14">
            <X className="w-4 h-4" data-id="element-15" />
          </button>
        </motion.div>}
    </AnimatePresence>;
};
const CountdownTimer = () => {
  const [timeLeft, setTimeLeft] = useState({
    h: 23,
    m: 59,
    s: 59
  });
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.s > 0) return {
          ...prev,
          s: prev.s - 1
        };
        if (prev.m > 0) return {
          h: prev.h,
          m: prev.m - 1,
          s: 59
        };
        if (prev.h > 0) return {
          h: prev.h - 1,
          m: 59,
          s: 59
        };
        return prev;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  return <div className="flex justify-center gap-4 text-center my-8" data-id="element-16">
      {[{
      label: 'HORAS',
      value: timeLeft.h
    }, {
      label: 'MINUTOS',
      value: timeLeft.m
    }, {
      label: 'SEGUNDOS',
      value: timeLeft.s
    }].map((unit, i) => <div key={i} className="flex flex-col items-center" data-id="element-17">
          <div className="bg-white text-brand-dark font-bold text-4xl md:text-5xl p-4 rounded-lg min-w-[80px] md:min-w-[100px] shadow-lg" data-id="element-18">
            {unit.value.toString().padStart(2, '0')}
          </div>
          <span className="text-xs md:text-sm font-bold mt-2 text-gray-300" data-id="element-19">
            {unit.label}
          </span>
        </div>)}
    </div>;
};
// --- Main App ---
export function App() {
  return <div className="min-h-screen bg-stone-50 font-poppins pb-20 md:pb-0" data-id="element-20">
      {/* Top Urgency Strip */}
      <div className="bg-brand-orange text-white text-center py-2 px-4 font-bold text-sm md:text-base sticky top-0 z-40 shadow-md" data-id="element-21">
        ⏰ OFERTA POR TIEMPO LIMITADO — 66% OFF HOY
      </div>

      {/* 1. HERO SECTION */}
      <Section className="pt-8 md:pt-16 bg-white text-center" data-id="element-22">
        <FadeIn data-id="element-23">
          <p className="text-gray-600 font-medium mb-6" data-id="element-24">
            +300 recetas saludables sin harinas y sin azúcar
          </p>

          <h1 className="text-4xl md:text-6xl font-black text-brand-dark leading-tight mb-8 text-center max-w-4xl mx-auto" data-id="element-25">
            Recuperá tu salud y vitalidad en
            <span className="block mt-3" data-id="element-26">
              <span className="inline-block bg-brand-orange text-white px-4 py-1.5 rounded-lg shadow-md transform -rotate-1 text-4xl md:text-5xl font-black tracking-tight" data-id="element-27">
                ¡SOLO 21 DÍAS!
              </span>
            </span>
          </h1>

          <a href={CHECKOUT_URL} className="block max-w-2xl mx-auto mb-8 hover:opacity-95 transition-opacity" data-id="element-28">
            <img src={HERO_IMAGE} alt="Recetario Saludable Ebook" className="w-full h-auto rounded-2xl shadow-2xl border-4 border-white" data-id="element-29" />
          </a>

          <h2 className="text-xl md:text-2xl text-gray-700 font-medium max-w-3xl mx-auto mb-6 leading-relaxed" data-id="element-30">
            Descubrí más de 300 recetas saludables, fáciles y deliciosas para
            dejar atrás las harinas y el azúcar sin sufrir dietas extremas.
          </h2>

          <p className="text-lg font-bold text-brand-green mb-8" data-id="element-31">
            ✨ Incluye 5 BONUS exclusivos de regalo.
          </p>

          <div className="max-w-2xl mx-auto text-left bg-stone-50 p-6 rounded-xl border border-stone-200 mb-8 shadow-sm" data-id="element-32">
            {['+300 recetas saludables fáciles de preparar', 'Sin harinas refinadas y sin azúcar', 'Comidas reales, ricas y saciantes', 'Ideal para bajar inflamación y sentir más energía', 'Formato digital con acceso inmediato', 'Compatible con celular, tablet y computadora'].map((bullet, i) => <div key={i} className="flex items-start gap-3 mb-3 last:mb-0" data-id="element-33">
                <CheckCircle2 className="w-6 h-6 text-brand-green shrink-0 mt-0.5" data-id="element-34" />
                <span className="text-gray-800 font-medium" data-id="element-35">{bullet}</span>
              </div>)}
          </div>

          <div className="mb-8" data-id="element-36">
            <p className="text-gray-400 line-through text-2xl font-bold mb-1" data-id="element-37">
              $49.990
            </p>
            <p className="text-5xl md:text-6xl font-black text-brand-orange" data-id="element-38">
              👉 HOY SOLO $16.990 ARS
            </p>
          </div>

          <CTAButton data-id="element-39">🥗 QUIERO MI RECETARIO COMPLETO</CTAButton>

          <div className="flex flex-wrap justify-center gap-4 md:gap-8 mt-6 text-sm md:text-base text-gray-600 font-medium" data-id="element-40">
            <span className="flex items-center gap-1" data-id="element-41">
              <Download className="w-4 h-4" data-id="element-42" /> Descarga inmediata
            </span>
            <span className="flex items-center gap-1" data-id="element-43">
              <Smartphone className="w-4 h-4" data-id="element-44" /> Acceso desde cualquier
              dispositivo
            </span>
            <span className="flex items-center gap-1" data-id="element-45">
              <ChefHat className="w-4 h-4" data-id="element-46" /> Recetas fáciles para todos los
              días
            </span>
          </div>
        </FadeIn>
      </Section>

      {/* 2. IDENTIFICATION BLOCK */}
      <Section className="bg-stone-100 border-y border-stone-200" data-id="element-47">
        <FadeIn data-id="element-48">
          <h2 className="text-3xl md:text-4xl text-center mb-10" data-id="element-49">
            ESTE LIBRO ES PARA VOS SI…
          </h2>
          <div className="max-w-2xl mx-auto space-y-4" data-id="element-50">
            {['Pensás todo el tiempo en lo que "no podés comer" y te frustrás', 'Tenés ansiedad y antojos de dulce constantemente', 'Sentís que la ropa cada vez te aprieta más', 'Vivís cansada, inflamada y sin energía', 'Querés mejorar tu alimentación pero no sabés qué cocinar', 'Empezás dietas y las abandonás rápido', 'Sentís culpa después de comer harinas o azúcar'].map((item, i) => <div key={i} className="flex items-start gap-4 bg-white p-4 rounded-lg shadow-sm" data-id="element-51">
                <XCircle className="w-6 h-6 text-red-500 shrink-0 mt-0.5" data-id="element-52" />
                <span className="text-gray-800 text-lg" data-id="element-53">{item}</span>
              </div>)}
          </div>
          <p className="text-center text-xl font-bold mt-10 text-brand-dark" data-id="element-54">
            👉 Si te sentís identificada aunque sea con una de estas cosas, este
            recetario puede ayudarte muchísimo.
          </p>
        </FadeIn>
      </Section>

      {/* 3. EMOTIONAL BLOCK */}
      <Section dark className="text-center" data-id="element-55">
        <FadeIn data-id="element-56">
          <h2 className="text-3xl md:text-5xl mb-8 text-white" data-id="element-57">
            El problema{' '}
            <span className="text-brand-orange" data-id="element-58">
              no es que no tengas voluntad.
            </span>
          </h2>
          <p className="text-xl md:text-2xl font-medium mb-10 text-gray-300" data-id="element-59">
            👉 El problema es que nadie te enseñó a comer saludable de una forma
            rica, práctica y sostenible.
          </p>

          <div className="bg-white/10 p-8 rounded-2xl max-w-xl mx-auto backdrop-blur-sm border border-white/20" data-id="element-60">
            <p className="text-xl font-bold mb-6 text-white" data-id="element-61">
              La mayoría de las dietas:
            </p>
            <ul className="text-left space-y-4 text-lg text-gray-200 mb-8" data-id="element-62">
              <li className="flex gap-3" data-id="element-63">
                <X className="text-red-400 shrink-0" data-id="element-64" /> te hacen pasar hambre
              </li>
              <li className="flex gap-3" data-id="element-65">
                <X className="text-red-400 shrink-0" data-id="element-66" /> te obligan a comer
                aburrido
              </li>
              <li className="flex gap-3" data-id="element-67">
                <X className="text-red-400 shrink-0" data-id="element-68" /> eliminan todo lo rico
              </li>
              <li className="flex gap-3" data-id="element-69">
                <X className="text-red-400 shrink-0" data-id="element-70" /> son imposibles de
                mantener
              </li>
            </ul>
            <p className="text-xl font-bold text-red-400 mb-8" data-id="element-71">
              Y por eso terminás abandonando.
            </p>
            <p className="text-2xl font-black text-brand-yellow" data-id="element-72">
              Este recetario fue creado justamente para lo contrario.
            </p>
          </div>
        </FadeIn>
      </Section>

      {/* 4 & 5. PRODUCT PRESENTATION & BENEFITS */}
      <Section data-id="element-73">
        <FadeIn data-id="element-74">
          <div className="text-center mb-10" data-id="element-75">
            <h2 className="text-4xl md:text-5xl mb-4" data-id="element-76">
              🥗 RECETARIO SALUDABLE
            </h2>
            <p className="text-2xl text-gray-600 font-bold mb-8" data-id="element-77">
              +300 recetas sin harinas y sin azúcar
            </p>
            <p className="text-xl max-w-2xl mx-auto mb-8" data-id="element-78">
              Una guía completa con recetas saludables diseñadas para ayudarte
              a:
            </p>
            <div className="flex flex-wrap justify-center gap-4 max-w-3xl mx-auto mb-8" data-id="element-79">
              {['comer rico', 'sentirte liviana', 'bajar inflamación', 'recuperar energía', 'dejar la dependencia del azúcar'].map((item, i) => <span key={i} className="bg-green-100 text-green-800 px-4 py-2 rounded-full font-bold flex items-center gap-2" data-id="element-80">
                  <Check className="w-4 h-4" data-id="element-81" /> {item}
                </span>)}
            </div>
            <p className="text-2xl font-black text-brand-orange" data-id="element-82">
              Sin sufrir ni complicarte.
            </p>
          </div>

          <div className="bg-stone-50 p-8 md:p-12 rounded-3xl border-2 border-brand-green/20" data-id="element-83">
            <h3 className="text-3xl text-center mb-10" data-id="element-84">
              🌟 ¿Qué vas a lograr con este ebook?
            </h3>
            <div className="grid md:grid-cols-2 gap-6" data-id="element-85">
              {['Vas a aprender a cocinar comidas saludables realmente ricas', 'Tendrás alternativas reales para dejar harinas y azúcar', 'Vas a sentir menos inflamación y pesadez', 'Vas a recuperar energía durante el día', 'Comer saludable dejará de sentirse como una "dieta"', 'Vas a tener recetas simples para desayuno, almuerzo, merienda y cena', 'Tendrás opciones dulces saludables para controlar la ansiedad', 'Ahorrarás tiempo pensando qué cocinar'].map((benefit, i) => <div key={i} className="flex items-start gap-4 bg-white p-4 rounded-xl shadow-sm" data-id="element-86">
                  <div className="bg-green-100 p-1 rounded-full shrink-0" data-id="element-87">
                    <CheckCircle2 className="w-6 h-6 text-brand-green" data-id="element-88" />
                  </div>
                  <span className="text-gray-800 font-medium text-lg leading-tight" data-id="element-89">
                    {benefit}
                  </span>
                </div>)}
            </div>
          </div>
        </FadeIn>
      </Section>

      {/* 6. VISUAL FOOD VARIETY */}
      <Section className="bg-stone-100" data-id="element-90">
        <FadeIn data-id="element-91">
          <h2 className="text-3xl md:text-4xl text-center mb-8" data-id="element-92">
            🥑 Variedad de recetas deliciosas para todos los días
          </h2>
          <div className="grid grid-cols-3 gap-3 md:gap-4 max-w-4xl mx-auto" data-id="element-93">
            {[{
            emoji: '🥞',
            text: 'Pancakes'
          }, {
            emoji: '🥗',
            text: 'Bowls'
          }, {
            emoji: '🥑',
            text: 'Desayunos'
          }, {
            emoji: '🍲',
            text: 'Sopas'
          }, {
            emoji: '🥤',
            text: 'Smoothies'
          }, {
            emoji: '🍕',
            text: 'Keto'
          }, {
            emoji: '🥘',
            text: 'Almuerzos'
          }, {
            emoji: '🍓',
            text: 'Postres'
          }, {
            emoji: '🥬',
            text: 'Antiinflam.'
          }].map((item, i) => <div key={i} className="aspect-square bg-white rounded-2xl shadow-sm text-center border border-stone-100 hover:shadow-md transition-shadow flex flex-col items-center justify-center p-2" data-id="element-94">
                <div className="text-3xl sm:text-4xl md:text-5xl mb-1 md:mb-2" data-id="element-95">
                  {item.emoji}
                </div>
                <p className="font-bold text-gray-800 text-xs sm:text-sm md:text-base leading-tight" data-id="element-96">
                  {item.text}
                </p>
              </div>)}
          </div>
        </FadeIn>
      </Section>

      {/* 7 & 8. DIFFERENTIATOR & RESULTS */}
      <Section data-id="element-97">
        <FadeIn data-id="element-98">
          <div className="text-center mb-10" data-id="element-99">
            <h2 className="text-3xl md:text-4xl mb-6" data-id="element-100">
              🧠 Este no es otro recetario aburrido de dieta.
            </h2>
            <p className="text-xl mb-8" data-id="element-101">👉 Acá vas a encontrar recetas:</p>
            <div className="flex flex-wrap justify-center gap-4" data-id="element-102">
              {['fáciles', 'rápidas', 'con ingredientes accesibles', 'visualmente apetitosas', 'pensadas para personas reales'].map((item, i) => <span key={i} className="bg-orange-100 text-brand-orange px-4 py-2 rounded-full font-bold flex items-center gap-2" data-id="element-103">
                  <Check className="w-4 h-4" data-id="element-104" /> {item}
                </span>)}
            </div>
          </div>

          <div className="bg-brand-dark text-white p-8 md:p-12 rounded-3xl shadow-2xl" data-id="element-105">
            <h3 className="text-3xl text-center mb-10 text-brand-yellow" data-id="element-106">
              🌱 Lo que podrías empezar a notar
            </h3>
            <div className="grid md:grid-cols-2 gap-6" data-id="element-107">
              {['Menos ansiedad por lo dulce', 'Más energía durante el día', 'Menos sensación de hinchazón', 'Mejor relación con la comida', 'Más saciedad y menos picoteo constante', 'Comer saludable sin sentir que estás "castigándote"'].map((result, i) => <div key={i} className="flex items-center gap-4 bg-white/10 p-4 rounded-xl" data-id="element-108">
                  <CheckCircle2 className="w-6 h-6 text-brand-green shrink-0" data-id="element-109" />
                  <span className="font-medium text-lg" data-id="element-110">{result}</span>
                </div>)}
            </div>
          </div>
        </FadeIn>
      </Section>

      {/* 9. MID-PAGE CTA */}
      <Section className="bg-stone-100 text-center py-12" data-id="element-111">
        <FadeIn data-id="element-112">
          <h2 className="text-2xl md:text-3xl mb-8 font-bold text-gray-800" data-id="element-113">
            ¿Lista para dar el primer paso hacia tu bienestar?
          </h2>
          <CTAButton data-id="element-114">👉 SÍ, QUIERO TRANSFORMAR MI ALIMENTACIÓN HOY</CTAButton>
        </FadeIn>
      </Section>

      {/* 10. BONUSES */}
      <Section className="bg-white" data-id="element-115">
        <FadeIn data-id="element-116">
          <div className="text-center mb-10" data-id="element-117">
            <h2 className="text-4xl md:text-5xl font-black text-brand-orange mb-4" data-id="element-118">
              🎁 BONUS (MUY IMPORTANTE)
            </h2>
            <p className="text-2xl font-bold text-gray-800" data-id="element-119">
              CON TU COMPRA RECIBÍS 5 BONUS EXCLUSIVOS
            </p>
          </div>

          <div className="space-y-12 max-w-5xl mx-auto" data-id="element-120">
            {[{
            num: 1,
            title: 'Cocina una vez, comé toda la semana',
            subtitle: 'Aprendé a organizar tus comidas fácilmente',
            bullets: ['Batch cooking', 'Organización semanal', 'Ahorro de tiempo'],
            image: 'https://cdn.magicpatterns.com/uploads/hazmbzFUEJkDFb4Ms6CAxP/ChatGPT_Image_21_may_2026,_17_03_03.png'
          }, {
            num: 2,
            title: 'Comida Proteica',
            subtitle: 'Recetas para sentirte saciada y con energía',
            bullets: ['Opciones altas en proteína', 'Almuerzos y cenas completas', 'Snacks saludables'],
            image: 'https://cdn.magicpatterns.com/uploads/gNx79Y45v1AHCnPBXSCcxE/ChatGPT_Image_21_may_2026,_17_04_10.png'
          }, {
            num: 3,
            title: 'Comidas Antiinflamatorias',
            subtitle: 'Recetas para desinflamar tu cuerpo desde adentro',
            bullets: ['Ingredientes naturales', 'Menos pesadez', 'Más bienestar digestivo'],
            image: 'https://cdn.magicpatterns.com/uploads/reToFfpSuwr7N98bw8MWGu/ChatGPT_Image_21_may_2026,_17_06_03.png'
          }, {
            num: 4,
            title: '50 Recetas 100% Veganas',
            subtitle: 'Opciones saludables basadas en plantas',
            bullets: ['Bowls', 'Snacks', 'Almuerzos y cenas'],
            image: 'https://cdn.magicpatterns.com/uploads/211EvLjtzh4ccoNuWvunEL/ChatGPT_Image_21_may_2026,_17_07_11.png'
          }, {
            num: 5,
            title: '50 Recetas 100% Vegetarianas',
            subtitle: 'Comidas saludables simples y nutritivas',
            bullets: ['Recetas prácticas', 'Ingredientes accesibles', 'Opciones rápidas'],
            image: 'https://cdn.magicpatterns.com/uploads/6Hy1GxTAijXUnTUAwQqQnK/ChatGPT_Image_21_may_2026,_17_08_21.png'
          }].map((bonus, i) => <div key={i} className="flex flex-col md:flex-row gap-8 items-center bg-stone-50 p-6 md:p-8 rounded-3xl border-2 border-stone-200" data-id="element-121">
                {/* Real Bonus Image */}
                <a href={CHECKOUT_URL} className="w-full md:w-1/3 aspect-square rounded-2xl shadow-xl overflow-hidden relative group block shrink-0 bg-white" data-id="element-122">
                  <img src={bonus.image} alt={`Bonus ${bonus.num}: ${bonus.title}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-123" />
                  <div className="absolute top-3 right-3 bg-brand-yellow text-brand-dark font-black px-3 py-1.5 rounded-full text-xs md:text-sm transform rotate-12 shadow-lg border-2 border-white" data-id="element-124">
                    GRATIS HOY
                  </div>
                </a>

                <div className="w-full md:w-2/3" data-id="element-125">
                  <div className="inline-block bg-brand-orange text-white font-bold px-4 py-1 rounded-full text-sm mb-4" data-id="element-126">
                    BONUS {bonus.num}
                  </div>
                  <h3 className="text-3xl font-black mb-2 text-gray-800" data-id="element-127">
                    {bonus.title}
                  </h3>
                  <p className="text-xl text-gray-600 font-medium mb-6" data-id="element-128">
                    👉 {bonus.subtitle}
                  </p>
                  <ul className="space-y-3" data-id="element-129">
                    {bonus.bullets.map((bullet, j) => <li key={j} className="flex items-center gap-3 text-lg" data-id="element-130">
                        <CheckCircle2 className="w-6 h-6 text-brand-green shrink-0" data-id="element-131" />
                        <span className="font-medium text-gray-700" data-id="element-132">
                          {bullet}
                        </span>
                      </li>)}
                  </ul>
                </div>
              </div>)}
          </div>

          <div className="mt-10 text-center bg-yellow-50 border-4 border-brand-yellow p-6 md:p-10 rounded-3xl max-w-3xl mx-auto" data-id="element-133">
            <h3 className="text-3xl md:text-4xl font-black mb-6" data-id="element-134">
              🎁 VALOR TOTAL DE LOS BONUS
            </h3>
            <p className="text-2xl text-gray-500 mb-2" data-id="element-135">Valor estimado:</p>
            <p className="text-4xl md:text-5xl font-black text-gray-400 line-through mb-6" data-id="element-136">
              $67.000 ARS
            </p>
            <p className="text-3xl md:text-5xl font-black text-brand-green highlight-yellow px-4 py-2 inline-block rounded-lg" data-id="element-137">
              👉 HOY VAN GRATIS CON TU COMPRA
            </p>
          </div>
        </FadeIn>
      </Section>

      {/* 11 & 12. REVIEWS */}
      <Section className="bg-stone-100" data-id="element-138">
        <FadeIn data-id="element-139">
          <div className="text-center mb-12" data-id="element-140">
            <h2 className="text-4xl md:text-5xl mb-4" data-id="element-141">💬 RESEÑAS REALES</h2>
            <p className="text-xl font-bold text-gray-600" data-id="element-142">
              Miles de personas ya están usando este recetario 🥗
            </p>
          </div>

          <div className="columns-1 md:columns-2 lg:columns-3 gap-6 space-y-6 max-w-7xl mx-auto" data-id="element-143">
            {[{
            name: 'Candela R.',
            text: 'Probé el batido proteico de chocolate y me ayudó muchísimo con la ansiedad por lo dulce.'
          }, {
            name: 'Mariela G.',
            text: 'No sabía cómo incorporar verduras en mis comidas. Ahora como sano sin complicarme.',
            image: 'https://cdn.magicpatterns.com/uploads/pcErL5YQLWb2r6a52PpMt2/A7aKPCDCN_mid.jpg'
          }, {
            name: 'Carolina H.',
            text: 'El bowl mexicano me salvó la cena. Rico, liviano y súper fácil.',
            image: 'https://cdn.magicpatterns.com/uploads/eaVVX1GMye8ELt6jBpeb3j/ZHxj92z7k_mid.jpg'
          }, {
            name: 'Verónica C.',
            text: 'Me hice el batido detox y literalmente me sentí menos inflamada.'
          }, {
            name: 'Patricia M.',
            text: 'Arranqué el gym a los 53 años y estas recetas me ayudaron muchísimo.',
            image: 'https://cdn.magicpatterns.com/uploads/sTp1ecaCpfptSmaq95ydQF/hq5NBFLB5_mid.jpg'
          }, {
            name: 'Carla B.',
            text: 'Hice el sándwich keto y me encantó. Hace rato buscaba dejar el pan.',
            image: 'https://cdn.magicpatterns.com/uploads/8kyNHHzHPjkxbKGdr4QSky/3HTkhalTG_mid.jpg'
          }, {
            name: 'Carlos M.',
            text: 'Me sorprendió lo rico que puede ser comer sin harinas.'
          }, {
            name: 'Lorena G.',
            text: 'La pizza de brócoli fue un antes y un después para mí.'
          }, {
            name: 'Diego R.',
            text: 'Hace rato quería cambiar mi alimentación porque estaba sin energía.'
          }, {
            name: 'Karina G.',
            text: 'Las recetas son súper fáciles y con ingredientes accesibles.'
          }, {
            name: 'Graciela D.',
            text: 'Lo mejor es que no te sentís a dieta.'
          }, {
            name: 'Natalia P.',
            text: 'Las opciones dulces saludables me salvaron muchísimo.'
          }, {
            name: 'Sofía M.',
            text: 'Todo está explicado simple y claro.'
          }, {
            name: 'Andrea T.',
            text: 'Me ayudó muchísimo a ordenar mis comidas.'
          }, {
            name: 'Mariana C.',
            text: 'Pensé que iba a ser difícil dejar el azúcar… pero con estas recetas fue mucho más fácil.'
          },
          // Extra Testimonials
          {
            name: 'María Fernanda L.',
            text: 'Desde que empecé con estas recetas me siento mucho más liviana y con energía. ¡Recomendadísimo!'
          }, {
            name: 'Rocío V.',
            text: 'A mis 52 años pensé que no iba a poder bajar de peso por la menopausia, pero dejar las harinas con estas opciones ricas fue la clave.'
          }, {
            name: 'Daniela P.',
            text: 'Lo mejor es que mis hijos también comen estas recetas y les encantan. ¡Chau a cocinar doble!',
            image: 'https://cdn.magicpatterns.com/uploads/gCgVoyAxjHdqHr2UrZvhmD/OC0hfm6hh_mid.jpg'
          }, {
            name: 'Susana E.',
            text: 'Mi médico me recomendó bajar el azúcar por prediabetes. Este recetario me salvó la vida, literalmente.'
          }, {
            name: 'Gabriela O.',
            text: 'Yo comía por ansiedad emocional. Tener opciones dulces saludables a mano evitó que recaiga en los ultraprocesados.'
          }].map((review: {
            name: string;
            text: string;
            image?: string;
          }, i) => <div key={i} className="bg-white rounded-2xl shadow-sm border border-stone-100 break-inside-avoid overflow-hidden" data-id="element-144">
                  {review.image && <div className="aspect-[4/3] overflow-hidden bg-stone-100" data-id="element-145">
                      <img src={review.image} alt={`Comida de ${review.name}`} className="w-full h-full object-cover" data-id="element-146" />
                    </div>}
                  <div className="p-6" data-id="element-147">
                    <div className="flex gap-1 mb-3" data-id="element-148">
                      {[...Array(5)].map((_, j) => <Star key={j} className="w-5 h-5 fill-yellow-400 text-yellow-400" data-id="element-149" />)}
                    </div>
                    <p className="text-gray-800 font-medium text-lg mb-4" data-id="element-150">
                      "{review.text}"
                    </p>
                    <p className="text-gray-500 font-bold text-sm" data-id="element-151">
                      — {review.name}
                    </p>
                  </div>
                </div>)}
          </div>
        </FadeIn>
      </Section>

      {/* 13. FAQ */}
      <Section className="bg-white" data-id="element-152">
        <FadeIn data-id="element-153">
          <h2 className="text-3xl md:text-4xl text-center mb-12" data-id="element-154">
            ❓ PREGUNTAS FRECUENTES
          </h2>
          <div className="max-w-3xl mx-auto space-y-6" data-id="element-155">
            {[{
            q: '¿Cómo recibo el ebook?',
            a: 'Lo recibís automáticamente por mail luego del pago.'
          }, {
            q: '¿Necesito saber cocinar?',
            a: 'No. Las recetas son simples y fáciles de seguir.'
          }, {
            q: '¿Voy a tener que dejar de comer rico?',
            a: 'Todo lo contrario. La idea es aprender a comer saludable disfrutando.'
          }, {
            q: '¿Los ingredientes son caros?',
            a: 'No. La mayoría son productos accesibles y fáciles de conseguir.'
          }, {
            q: '¿Funciona aunque tenga poco tiempo?',
            a: 'Sí. Muchas recetas son rápidas y prácticas.'
          }, {
            q: '¿Puedo verlo desde el celular?',
            a: 'Sí. Compatible con celular, tablet y PC.'
          }].map((faq, i) => <div key={i} className="bg-stone-50 p-6 rounded-xl border border-stone-200" data-id="element-156">
                <h4 className="text-xl font-bold text-brand-dark mb-2" data-id="element-157">
                  {faq.q}
                </h4>
                <p className="text-gray-600 font-medium text-lg" data-id="element-158">👉 {faq.a}</p>
              </div>)}
          </div>
        </FadeIn>
      </Section>

      {/* 14 & 15. URGENCY & FINAL OFFER */}
      <Section dark className="text-center pb-24 md:pb-32" data-id="element-159">
        <FadeIn data-id="element-160">
          <div className="inline-block bg-red-600 text-white font-black px-6 py-2 rounded-full text-xl md:text-2xl mb-8 animate-pulse" data-id="element-161">
            ⚠️ ESTA OFERTA ES POR TIEMPO LIMITADO
          </div>

          <CountdownTimer data-id="element-162" />

          <div className="bg-white text-brand-dark p-8 md:p-12 rounded-3xl max-w-3xl mx-auto mt-12 shadow-2xl" data-id="element-163">
            <h3 className="text-3xl font-black mb-8" data-id="element-164">Hoy te llevás:</h3>
            <ul className="text-left space-y-4 mb-10 max-w-md mx-auto" data-id="element-165">
              {['+300 recetas saludables', 'Opciones sin harinas y sin azúcar', 'Los 5 bonus exclusivos', 'Acceso inmediato', 'Formato digital'].map((item, i) => <li key={i} className="flex items-center gap-3 text-lg font-bold text-gray-700" data-id="element-166">
                  <CheckCircle2 className="w-6 h-6 text-brand-green shrink-0" data-id="element-167" />{' '}
                  {item}
                </li>)}
            </ul>

            <div className="mb-10 p-6 bg-stone-50 rounded-2xl border-2 border-dashed border-stone-300" data-id="element-168">
              <p className="text-xl font-bold text-gray-500 mb-2" data-id="element-169">
                TODO ESTO POR SOLO:
              </p>
              <p className="text-3xl text-gray-400 line-through font-bold mb-2" data-id="element-170">
                $49.990
              </p>
              <p className="text-5xl md:text-6xl font-black text-brand-orange" data-id="element-171">
                👉 HOY $16.990 ARS
              </p>
            </div>

            <CTAButton className="text-2xl py-6" data-id="element-172">
              🥑 QUIERO EMPEZAR A COMER MEJOR HOY
            </CTAButton>

            <div className="flex flex-wrap justify-center gap-4 md:gap-6 mt-6 text-sm font-bold text-gray-500" data-id="element-173">
              <span className="flex items-center gap-1" data-id="element-174">
                <Download className="w-4 h-4" data-id="element-175" /> Descarga inmediata
              </span>
              <span className="flex items-center gap-1" data-id="element-176">
                <Smartphone className="w-4 h-4" data-id="element-177" /> Compatible con cualquier
                dispositivo
              </span>
              <span className="flex items-center gap-1" data-id="element-178">
                <ChefHat className="w-4 h-4" data-id="element-179" /> Recetas fáciles y deliciosas
              </span>
            </div>
          </div>
        </FadeIn>
      </Section>

      {/* 16. FOOTER */}
      <footer className="bg-stone-900 text-stone-400 py-12 text-center text-sm" data-id="element-180">
        <div className="max-w-4xl mx-auto px-4 flex flex-col md:flex-row justify-center items-center gap-6 md:gap-12" data-id="element-181">
          <span className="flex items-center gap-2" data-id="element-182">
            <ShieldCheck className="w-5 h-5" data-id="element-183" /> 🔒 Compra segura
          </span>
          <span className="flex items-center gap-2" data-id="element-184">
            <Clock className="w-5 h-5" data-id="element-185" /> 📩 Acceso inmediato
          </span>
          <span className="flex items-center gap-2" data-id="element-186">
            <CheckCircle2 className="w-5 h-5" data-id="element-187" /> 🥗 Diseñado para personas
            reales que quieren sentirse mejor
          </span>
        </div>
      </footer>

      {/* Mobile Sticky CTA */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white p-4 border-t border-gray-200 shadow-[0_-10px_20px_rgba(0,0,0,0.1)] z-40" data-id="element-188">
        <div className="flex items-center justify-between mb-2 px-2" data-id="element-189">
          <span className="font-bold text-gray-500 line-through text-sm" data-id="element-190">
            $49.990
          </span>
          <span className="font-black text-brand-orange text-lg" data-id="element-191">
            $16.990 ARS
          </span>
        </div>
        <CTAButton className="py-3 text-lg" data-id="element-192">🥗 QUIERO MI RECETARIO</CTAButton>
      </div>

      <LivePopup data-id="element-193" />
    </div>;
}